import styled from 'styled-components';
import { Menu, MenuItemsType } from 'components/Toolkit/Menu/Menu';
import {
  CardOutlineIc,
  ChatboxOutlineIc,
  CreateOutlineIc,
  HeartOutlineIc,
  HelpCircleOutlineIc,
  LogInOutlineIc,
  LogOutOutlineIc,
  PersonCircleOutlineIc,
  StarOutlineIc,
  TimeOutlineIc,
} from '@dsch/dd-icons';
import { theme } from 'helpers/Theme';

export enum USER_MENU {
  MY_DONE_DEAL = 'mydonedeal',
  MY_ADS = 'myads',
  MESSAGES = 'messages',
  SAVED = 'saved',
  SEARCHES = 'searches',
  HISTORY_CHECKS = 'history-checks',
  PAYMENTS = 'payments',
  USER_LOGOUT = 'log_out',
  USER_LOGIN = 'login',
  USER_SIGN_UP = 'sign_up',
  HELP = 'help',
  BROWSING_HISTORY = 'browsing-history',
}

const UserMenuContainer = styled.div``;

export interface IUserMenu {
  baseUrl: string;
  cdnUrl: string;
  messages?: number;
  historyChecks?: number;
  selected?: USER_MENU;
  user?: {
    isUserLogged: boolean;
    onUserLogOut: () => void;
    onUserLogIn: () => void;
    onSignUp: () => void;
  };
  help?: boolean;
  variant?: 'DASHBOARD';
  selectedSection?: string;
  includeNextLink?: boolean;
  legacyAuth?: boolean;
}

const PanelIcon = styled.img`
  width: 20px;
  height: 20px;
`;

const UserMenu = ({
  baseUrl,
  cdnUrl,
  messages,
  historyChecks,
  selected,
  user,
  help = false,
  variant,
  selectedSection,
  includeNextLink,
  legacyAuth,
}: IUserMenu) => {
  const menuItems: MenuItemsType = [
    {
      id: USER_MENU.MY_DONE_DEAL,
      selected: selected === USER_MENU.MY_DONE_DEAL,
      displayName: 'My DoneDeal',
      link: `/dashboard/${USER_MENU.MY_DONE_DEAL}`,
      icon: (
        <PersonCircleOutlineIc
          color={theme.colors.GREY_LIGHT}
          width="20"
          height="20"
        />
      ),
      includeNextLink,
    },
    {
      id: USER_MENU.MY_ADS,
      selected: selected === USER_MENU.MY_ADS,
      displayName: 'My Ads',
      link: `/dashboard/${USER_MENU.MY_ADS}`,
      icon: (
        <CreateOutlineIc color={theme.colors.GREEN} width="20" height="20" />
      ),
      includeNextLink,
    },
    {
      id: USER_MENU.MESSAGES,
      selected: selected === USER_MENU.MESSAGES,
      displayName: 'Messages',
      link: `${baseUrl}/accounts/classifieds/${USER_MENU.MESSAGES}`,
      notifications: messages && messages > 0 ? messages : null,
      icon: (
        <ChatboxOutlineIc color={theme.colors.BLUE} width="20" height="20" />
      ),
    },
    {
      id: USER_MENU.SAVED,
      selected: selected === USER_MENU.SAVED,
      displayName: 'Saved Ads',
      link: `/dashboard/${USER_MENU.SAVED}`,
      icon: (
        <HeartOutlineIc color={theme.colors.RED_LIGHT} width="20" height="20" />
      ),
      includeNextLink,
    },
    {
      id: USER_MENU.SEARCHES,
      selected: selected === USER_MENU.SEARCHES,
      displayName: 'Saved Searches',
      link: `/dashboard/${USER_MENU.SEARCHES}`,
      icon: (
        <StarOutlineIc color={theme.colors.ORANGE} width="20" height="20" />
      ),
      includeNextLink,
    },
    {
      id: USER_MENU.BROWSING_HISTORY,
      selected: selected === USER_MENU.BROWSING_HISTORY,
      displayName: 'Browsing History',
      link: `/dashboard/${USER_MENU.BROWSING_HISTORY}`,
      icon: <TimeOutlineIc color={theme.colors.BLUE} width="20" height="20" />,
      includeNextLink,
    },
    {
      id: USER_MENU.HISTORY_CHECKS,
      selected: selected === USER_MENU.HISTORY_CHECKS,
      displayName: 'History Checks',
      link: `${baseUrl}/accounts/classifieds/${USER_MENU.HISTORY_CHECKS}`,
      notifications: historyChecks && historyChecks > 0 ? historyChecks : null,
      icon: (
        <PanelIcon
          src={`${cdnUrl}/images/illustrations/greenlight.svg`}
          width="20"
          height="20"
        />
      ),
    },
    {
      id: USER_MENU.PAYMENTS,
      selected: selected === USER_MENU.PAYMENTS,
      displayName: 'Payment History',
      link: `/dashboard/${USER_MENU.PAYMENTS}`,
      icon: (
        <CardOutlineIc color={theme.colors.BLUE_DARK} width="20" height="20" />
      ),
      includeNextLink,
    },
  ];

  user &&
    user.isUserLogged &&
    user.onUserLogOut &&
    menuItems.push({
      id: USER_MENU.USER_LOGOUT,
      selected: selected === USER_MENU.USER_LOGOUT,
      displayName: 'Log out',
      onClick: user.onUserLogOut,
      icon: <LogOutOutlineIc color={theme.colors.GREY} />,
    });

  user &&
    !user.isUserLogged &&
    user.onUserLogIn &&
    user.onSignUp &&
    legacyAuth &&
    menuItems.push([
      {
        id: USER_MENU.USER_LOGIN,
        selected: selected === USER_MENU.USER_LOGIN,
        displayName: 'Log in',
        onClick: user.onUserLogIn,
        icon: <LogInOutlineIc color={theme.colors.GREY} />,
      },
      {
        id: USER_MENU.USER_SIGN_UP,
        selected: selected === USER_MENU.USER_SIGN_UP,
        displayName: 'Sign up',
        onClick: user.onSignUp,
      },
    ]);

  user &&
    !user.isUserLogged &&
    user.onUserLogIn &&
    !legacyAuth &&
    menuItems.push({
      id: USER_MENU.USER_LOGIN,
      selected: selected === USER_MENU.USER_LOGIN,
      displayName: 'Log in',
      onClick: user.onUserLogIn,
      icon: <LogInOutlineIc color={theme.colors.GREY} />,
    });

  help &&
    menuItems.push({
      id: USER_MENU.HELP,
      selected: selected === USER_MENU.HELP,
      displayName: 'Help',
      link: `https://hello.donedeal.ie/hc/en-us/`,
      newTab: true,
      icon: <HelpCircleOutlineIc color={theme.colors.BLUE} />,
      divider: true,
    });

  return (
    <UserMenuContainer>
      <Menu
        items={menuItems}
        icons={true}
        variant={variant}
        selectedSection={selectedSection}
      />
    </UserMenuContainer>
  );
};

export { UserMenu };
